import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  CardActions,
  Button,
  Chip,
  CircularProgress,
  Alert,
  AlertTitle,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  FormHelperText,
  InputLabel,
  LinearProgress,
  Select,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  IconButton,
  Link as MuiLink,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip
} from '@mui/material';
import {
  OpenInNew as OpenInNewIcon,
  PersonAdd as PersonAddIcon,
  Add as AddIcon,
  Link as LinkIcon,
  ContentCopy as ContentCopyIcon,
  SmartToy as SmartToyIcon,
  AccountBalanceWallet as WalletIcon,
  People as PeopleIcon,
  DeleteOutline as DeleteOutlineIcon,
  Refresh as RefreshIcon,
  Handshake as HandshakeIcon,
  Storage as StorageIcon,
  MoreVert as MoreVertIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useCustomerAuth } from '../../contexts/CustomerAuthContext';
import CustomerLayout from '../../components/customer/CustomerLayout';
import AccountHelpPanel from '../../components/customer/AccountHelpPanel';
// Proyección de saldo: la única cifra del panel que mira hacia delante.
import CreditProjectionCard from '../../components/customer/CreditProjectionCard';
import EmployeeStatusChip from '../../components/EmployeeStatusChip';
import AccountUserStatusChip from '../../components/AccountUserStatusChip';
// Desglose «dos bolsas» (Plan / Extra / Total) + los helpers de etiqueta legible
// del kind y de fecha-con-hora, compartidos con la página de historial para que
// las dos cuenten lo mismo.
import CreditBreakdown, {
  creditKindLabel,
  formatCreditDateTime
} from '../../components/customer/CreditBreakdown';
import EmployeeCatalogLink from '../../components/EmployeeCatalogLink';
// Elección de los DOS volúmenes del empleado al contratarlo, con su impacto en
// vivo (incluido consumido, facturable y créditos por ciclo). Compartida con el
// portal partner y el panel admin: las reglas de tamaño son las del backend y se
// deciden en un solo sitio.
import EmployeeSizePicker, {
  DEFAULT_EMPLOYEE_SIZE, EmployeeSize, EmployeeSizeChip
} from '../../components/EmployeeSizePicker';
import EmployeeStoragePicker, {
  EmployeeVolumes,
  defaultVolumes,
  storageImpact
} from '../../components/EmployeeStoragePicker';
import customerApiService from '../../services/customerApi';
// El catálogo público de empleados (/public/agent-employees) vive en el servicio
// general: mismo picker por NOMBRE que usa el admin en ClientAccountDetailPage.
import { apiService } from '../../services/api';
import {
  AccountEmployee,
  AccountStorage,
  AccountUser,
  AccountUserAuthMethod,
  AccountUserCheck,
  CreditEntry,
  CreditPlanFields,
  CreditsPeriod,
  CustomerAccountSummary,
  CustomerCreditsResponse,
  CustomerHireEmployeeRequest,
  EmployeeUpdate,
  PublicListingOption,
  UserAssignment
} from '../../types/clientAccount';
import { CreditProjection } from '../../types/creditPackage';
// Lectura ÚNICA del veredicto del tenant sobre el disco (applied/deferred/failed),
// compartida con los portales de admin y partner: los tres cuentan lo mismo.
import { storageSyncFeedback } from '../../utils/storageSync';

const formatCredits = (value: number | string | null | undefined): string => {
  if (value === null || value === undefined || value === '') return '0.00';
  const num = Number(value);
  return Number.isNaN(num) ? String(value) : num.toFixed(2);
};

const toNum = (value: number | string | null | undefined): number | null => {
  if (value === null || value === undefined || value === '') return null;
  const num = Number(value);
  return Number.isNaN(num) ? null : num;
};

// ── Almacenamiento: el tramo y su reparto ─────────────────────────────────
// Un empleado ocupa DOS volúmenes reales (workspace del agente + datos de su
// app), 10+10 GiB de salida porque 10 es el mínimo del proveedor. Las
// ampliaciones van en TRAMOS de `step` GiB (20 por defecto, dato que manda el
// backend), y el cliente elige DÓNDE cae ese tramo: a partes iguales, todo al
// espacio de trabajo o todo a los datos. Cualquiera de las tres opciones suma
// exactamente un tramo, así que ninguna puede dejar un tamaño inválido.
type StorageSplit = 'both' | 'workspace' | 'data';

const DEFAULT_VOLUME_GIB = 10;

const employeeVolumes = (emp: AccountEmployee): { ws: number; data: number } => ({
  ws: Number(emp.workspace_gib ?? DEFAULT_VOLUME_GIB),
  data: Number(emp.data_gib ?? DEFAULT_VOLUME_GIB)
});

const nextVolumes = (
  emp: AccountEmployee,
  split: StorageSplit,
  step: number
): { workspace_gib: number; data_gib: number } => {
  const { ws, data } = employeeVolumes(emp);
  if (split === 'workspace') return { workspace_gib: ws + step, data_gib: data };
  if (split === 'data') return { workspace_gib: ws, data_gib: data + step };
  // A partes iguales. Con un tramo impar (configuración rara pero posible) el
  // reparto se desequilibra en 1 GiB en vez de romper el total del tramo.
  return {
    workspace_gib: ws + Math.ceil(step / 2),
    data_gib: data + Math.floor(step / 2)
  };
};

// «Consumido (últimos 30 días): X de Y» + barrita de progreso del empleado.
// Vive SOLO en la tarjeta del empleado: el bloque de créditos de la CUENTA no
// se duplica aquí (ese está una vez, en la sección «Créditos»).
//
// El consumo viene best-effort de la propia app desplegada; el límite, del
// límite APLICADO en ella (budget_limit) y, si el tenant aún no lo devuelve,
// del límite pactado en el marketplace (allocated_credits — puede diferir
// hasta el próximo deploy). Sin consumo NO se calla la línea: se pinta «—» y
// se explica que el dato aún no está disponible (app parada/arrancando), que
// es justo lo que confundía cuando la línea desaparecía entera. La etiqueta
// lleva SIEMPRE su periodo: la ventana del presupuesto de la app son 30 días
// rodantes.
const EmployeeConsumption: React.FC<{ emp: AccountEmployee }> = ({ emp }) => {
  const { t } = useTranslation();
  const consumed = toNum(emp.consumed_30d);
  const applied = toNum(emp.budget_limit);
  const agreed = toNum(emp.allocated_credits);
  const limit = applied !== null && applied > 0 ? applied : (agreed !== null && agreed > 0 ? agreed : null);
  // Empleado que aún no existe en el tenant: nada que consumir todavía.
  if (emp.status !== 'active') return null;
  const pct =
    consumed !== null && limit !== null && limit > 0
      ? Math.min(100, (consumed / limit) * 100)
      : null;
  return (
    <Box mt={1}>
      <Typography variant="body2" color="text.secondary">
        {t('customer.account.consumed30d', 'Consumido (últimos 30 días)')}:{' '}
        <Typography component="span" variant="body2" fontWeight={600} color="text.primary">
          {consumed === null ? '—' : formatCredits(consumed)}
        </Typography>
        {limit !== null && (
          <Typography component="span" variant="body2" color="text.secondary">
            {' '}
            {t('customer.account.ofLimit', 'de {{limit}}', { limit: formatCredits(limit) })}
          </Typography>
        )}
      </Typography>
      {pct !== null && (
        <LinearProgress
          variant="determinate"
          value={pct}
          color={pct >= 100 ? 'error' : pct >= 80 ? 'warning' : 'primary'}
          sx={{ mt: 0.5, height: 6, borderRadius: 3 }}
        />
      )}
      {consumed === null && (
        <Typography variant="caption" color="text.secondary">
          {t('customer.account.consumptionUnavailable', 'Dato aún no disponible (el empleado se está iniciando o está parado)')}
        </Typography>
      )}
    </Box>
  );
};

// Fila de acciones al pie de la tarjeta de un empleado. Antes eran cuatro
// botones con textos largos («Fijar límite mensual», «Ampliar almacenamiento
// (+20 GiB)») en línea: en una tarjeta estrecha se amontonaban, partían el texto
// y se solapaban. Ahora la acción principal («Abrir») queda a la izquierda, las
// dos secundarias se recogen en un menú de overflow (⋮) —para que la tarjeta se
// vea ordenada a cualquier ancho— y la destructiva («Eliminar», en rojo) se
// empuja a la derecha, separada del resto. El contenedor lleva flexWrap+gap por
// si aún así no cabe: envuelve limpio en vez de solaparse.
interface EmployeeActionsProps {
  emp: AccountEmployee;
  readOnly: boolean;
  storageStep: number;
  onAllocate: (emp: AccountEmployee) => void;
  onExpandStorage: (emp: AccountEmployee) => void;
  onRemove: (emp: AccountEmployee) => void;
}

const EmployeeActions: React.FC<EmployeeActionsProps> = ({
  emp,
  readOnly,
  storageStep,
  onAllocate,
  onExpandStorage,
  onRemove
}) => {
  const { t } = useTranslation();
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const closeMenu = () => setMenuAnchor(null);
  const isRemoved = emp.status === 'removed';

  return (
    <CardActions sx={{ px: 2, pb: 2, gap: 1, flexWrap: 'wrap' }}>
      {emp.status === 'active' && emp.app_url && (
        <Button
          variant="contained"
          size="small"
          endIcon={<OpenInNewIcon />}
          onClick={() => window.open(emp.app_url as string, '_blank', 'noopener,noreferrer')}
          sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 2, boxShadow: 'none' }}
        >
          {t('customer.account.openEmployee', 'Abrir')}
        </Button>
      )}
      {!isRemoved && (
        <>
          {/* Overflow: las dos acciones de texto largo (límite mensual y
              ampliar almacenamiento) viven aquí para no romper la fila. */}
          <Tooltip title={t('customer.account.moreActions', 'Más acciones')}>
            <span>
              <IconButton
                size="small"
                onClick={(e) => setMenuAnchor(e.currentTarget)}
                disabled={readOnly}
                aria-label={t('customer.account.moreActions', 'Más acciones')}
              >
                <MoreVertIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={closeMenu}>
            <MenuItem
              onClick={() => {
                closeMenu();
                onAllocate(emp);
              }}
            >
              <ListItemIcon>
                <WalletIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>{t('customer.account.allocateCredits', 'Fijar límite mensual')}</ListItemText>
            </MenuItem>
            <MenuItem
              onClick={() => {
                closeMenu();
                onExpandStorage(emp);
              }}
            >
              <ListItemIcon>
                <StorageIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>
                {t('customer.account.expandStorageShort', 'Ampliar almacenamiento (+{{step}} GiB)', {
                  step: storageStep
                })}
              </ListItemText>
            </MenuItem>
          </Menu>
          {/* Baja del empleado: retira su aplicación y LIBERA el cupo. A la
              derecha del todo y en rojo — es irreversible. */}
          <Button
            size="small"
            color="error"
            startIcon={<DeleteOutlineIcon />}
            onClick={() => onRemove(emp)}
            disabled={readOnly}
            sx={{ textTransform: 'none', ml: 'auto' }}
          >
            {t('customer.account.removeEmployee', 'Eliminar')}
          </Button>
        </>
      )}
    </CardActions>
  );
};

const CustomerAccountPage: React.FC = () => {
  const { t } = useTranslation();
  // Modo impersonación (partner «viendo como cliente»): TODA acción de
  // escritura se deshabilita — el banner fijo del layout ya lo anuncia.
  const { readOnly } = useCustomerAuth();
  const navigate = useNavigate();

  const [account, setAccount] = useState<CustomerAccountSummary | null>(null);
  const [hasAccount, setHasAccount] = useState<boolean>(true);
  const [employees, setEmployees] = useState<AccountEmployee[]>([]);
  // Aviso «hay versión superior en el marketplace» por empleado desplegado (best-effort).
  const [employeeUpdates, setEmployeeUpdates] = useState<EmployeeUpdate[]>([]);
  const [upgradingEmployeeId, setUpgradingEmployeeId] = useState<number | null>(null);
  const [users, setUsers] = useState<AccountUser[]>([]);
  const [creditBalance, setCreditBalance] = useState<number | string>(0);
  const [creditEntries, setCreditEntries] = useState<CreditEntry[]>([]);
  // Respuesta completa de /account/credits: trae (si el backend ya los emite)
  // los campos «dos bolsas» del plan; con campos ausentes la UI cae al saldo simple.
  const [creditsInfo, setCreditsInfo] = useState<CustomerCreditsResponse | null>(null);
  // Proyección de saldo («a este ritmo llegas hasta el {fecha}»). null = no hay
  // dato o no hay histórico suficiente ⇒ la tarjeta NO se pinta: una fecha
  // calculada sobre dos días de uso baila en cada recarga y hace daño.
  const [projection, setProjection] = useState<CreditProjection | null>(null);
  // Periodo de la sección Créditos (Este ciclo / Últimos 30 días / Todo). El
  // ref lo lee loadAll (callback estable) para que el sondeo silencioso no
  // pise la selección del usuario al refrescar.
  const [creditsPeriod, setCreditsPeriod] = useState<CreditsPeriod>('all');
  const creditsPeriodRef = useRef<CreditsPeriod>('all');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Añadir usuario
  const [addUserDialogOpen, setAddUserDialogOpen] = useState(false);
  const [addUserLoading, setAddUserLoading] = useState(false);
  // Error del alta DENTRO del diálogo: el motivo (409 del backend) se enseña
  // junto al formulario que lo provocó y con los datos aún escritos — cerrar el
  // diálogo y sacar una barra arriba obligaba a rellenarlo todo otra vez.
  const [addUserError, setAddUserError] = useState<string | null>(null);
  const [newUserEmail, setNewUserEmail] = useState('');
  const [newUserName, setNewUserName] = useState('');
  // Método de autenticación del usuario nuevo: solo Google ⇒ sin contraseña.
  const [newUserAuthMethod, setNewUserAuthMethod] = useState<AccountUserAuthMethod>('password');
  // Resultado del check del email (al perder el foco): si ya existe en el
  // tenant, el alta NO crea a nadie ni genera contraseña.
  const [emailCheck, setEmailCheck] = useState<AccountUserCheck | null>(null);
  const [checkingEmail, setCheckingEmail] = useState(false);
  // Contraseña generada — se enseña UNA sola vez
  const [generatedPassword, setGeneratedPassword] = useState<{ email: string; password: string } | null>(null);
  // Alta SIN contraseña que enseñar (solo-Google o usuario ya existente): el
  // portal debe decir qué ha pasado en vez de no mostrar nada.
  const [userCreatedNotice, setUserCreatedNotice] = useState<string | null>(null);

  // Baja de un empleado (libera cupo) — confirmación explícita
  const [removeTarget, setRemoveTarget] = useState<AccountEmployee | null>(null);
  const [removeLoading, setRemoveLoading] = useState(false);

  // Refresco manual del consumo de la cuenta (el sync del GET va con throttle)
  const [refreshingConsumption, setRefreshingConsumption] = useState(false);

  // Asignar usuario a empleado
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [assignLoading, setAssignLoading] = useState(false);
  const [userToAssign, setUserToAssign] = useState<AccountUser | null>(null);
  const [assignEmployeeId, setAssignEmployeeId] = useState<number | ''>('');
  const [assignRole, setAssignRole] = useState<'member' | 'admin'>('member');
  // Error INLINE de la asignación: el backend responde 409 con el motivo
  // concreto (p. ej. «Ana ya está asignada a Odoo como miembro») y se pinta
  // dentro del diálogo, sin cerrarlo, para poder corregir la elección ahí mismo.
  const [assignError, setAssignError] = useState<string | null>(null);

  // Desasignar usuario de un empleado (la X del chip): confirmación ligera
  // antes del DELETE; los 4xx del backend se enseñan por toast con su detail.
  const [unassignTarget, setUnassignTarget] = useState<{
    user: AccountUser;
    assignment: UserAssignment;
  } | null>(null);
  const [unassignLoading, setUnassignLoading] = useState(false);
  // Mismo criterio que en asignar: el motivo real (404/502 del backend) se
  // queda en la confirmación, que sigue abierta para reintentar o cancelar.
  const [unassignError, setUnassignError] = useState<string | null>(null);

  // Contratar empleado — picker por NOMBRE del catálogo público (rellena el slug)
  const [hireDialogOpen, setHireDialogOpen] = useState(false);
  const [hireLoading, setHireLoading] = useState(false);
  const [hireError, setHireError] = useState<string | null>(null);
  const [listingOptions, setListingOptions] = useState<PublicListingOption[]>([]);
  const [loadingListings, setLoadingListings] = useState(false);
  const [hireListingSlug, setHireListingSlug] = useState('');
  const [hireDisplayName, setHireDisplayName] = useState('');
  const [hireCredits, setHireCredits] = useState('');
  // ADMINISTRADOR del empleado: uno de TUS usuarios (obligatorio). Es quien
  // administra el empleado y la identidad con la que este trabaja — tu login de
  // cliente sirve solo para este portal, no para la plataforma.
  const [hireAdminUserId, setHireAdminUserId] = useState<number | ''>('');
  // TAMAÑO de los dos volúmenes del empleado que se va a contratar. Se elige
  // aquí y no después porque ampliar es permanente y con cargo: nacer con el
  // tamaño bueno es gratis, crecer luego no siempre. Los mínimos del backend
  // son la salida (10+10), y el picker no deja elegir un par inválido.
  const [hireVolumes, setHireVolumes] = useState<EmployeeVolumes>({
    workspace_gib: 10, data_gib: 10
  });
  // CAPACIDAD (talla) del empleado. Sale marcada la que la cuenta traiga por
  // defecto —la del partner que la gestiona, si lo hay— y se puede cambiar.
  const [hireSize, setHireSize] = useState<EmployeeSize>(DEFAULT_EMPLOYEE_SIZE);

  // Ampliar el almacenamiento de un empleado: es PERMANENTE y con coste por
  // ciclo mientras el empleado exista, así que nunca se hace de un clic — se
  // confirma en un diálogo que dice el tamaño resultante y lo que costará.
  const [storageTarget, setStorageTarget] = useState<AccountEmployee | null>(null);
  const [storageSplit, setStorageSplit] = useState<StorageSplit>('both');
  const [storageLoading, setStorageLoading] = useState(false);
  const [storageError, setStorageError] = useState<string | null>(null);

  // Asignar créditos a un empleado (se aplican al (re)desplegarlo)
  const [allocateDialogOpen, setAllocateDialogOpen] = useState(false);
  const [allocateLoading, setAllocateLoading] = useState(false);
  const [allocateError, setAllocateError] = useState<string | null>(null);
  const [employeeToAllocate, setEmployeeToAllocate] = useState<AccountEmployee | null>(null);
  const [allocateValue, setAllocateValue] = useState('');

  const pollTimer = useRef<number | null>(null);

  // Época de la lista de usuarios. loadAll es LENTO por naturaleza (su GET de
  // cuenta sincroniza consumo contra el tenant y el de empleados consulta el
  // presupuesto de cada app con 5 s de timeout), y además hay un sondeo
  // silencioso cada 8 s mientras se despliega algo: una respuesta suya EN VUELO
  // cuando se asigna/desasigna un usuario pisaría la lista recién refrescada con
  // la de ANTES de la mutación — exactamente el «no se actualiza hasta recargar».
  // Cada mutación de usuarios incrementa la época; quien empezó antes ya no
  // escribe.
  const usersEpochRef = useRef(0);

  // Refresco SOLO de usuarios: una llamada rápida que se puede esperar tras
  // asignar/desasignar sin arrastrar el resto del panel.
  const reloadUsers = useCallback(async () => {
    const epoch = ++usersEpochRef.current;
    try {
      const data = await customerApiService.getMyAccountUsers();
      if (epoch === usersEpochRef.current) setUsers(data);
    } catch (err) {
      console.error('Failed to reload account users:', err);
    }
  }, []);

  const loadAll = useCallback(async (silent: boolean = false) => {
    try {
      if (!silent) setError(null);
      const accountData = await customerApiService.getMyAccount();
      if (accountData === null) {
        // EndUser sin cuenta de empleados IA: estado vacío amable, nunca romper.
        setHasAccount(false);
        return;
      }
      setHasAccount(true);
      setAccount(accountData);
      const usersEpoch = usersEpochRef.current;
      const [employeesData, usersData, creditsData] = await Promise.all([
        customerApiService.getMyAccountEmployees(),
        customerApiService.getMyAccountUsers(),
        customerApiService.getMyAccountCredits(creditsPeriodRef.current)
      ]);
      setEmployees(employeesData);
      // Aviso de actualización: best-effort y aparte (un backend sin el endpoint no puede
      // dejar sin panel a nadie — simplemente no se pinta el banner).
      try {
        setEmployeeUpdates(await customerApiService.getMyEmployeeUpdates());
      } catch (updErr) {
        console.error('Failed to load employee updates:', updErr);
        setEmployeeUpdates([]);
      }
      // Solo si nadie tocó las asignaciones mientras esta carga estaba en vuelo.
      if (usersEpoch === usersEpochRef.current) setUsers(usersData);
      setCreditBalance(creditsData.balance);
      setCreditEntries(creditsData.entries);
      setCreditsInfo(creditsData);
      // Proyección: best-effort y SIEMPRE aparte del resto. Un backend que aún
      // no tenga el endpoint (o un fallo suyo) no puede dejar sin panel a
      // nadie — la tarjeta simplemente no se pinta.
      try {
        setProjection(await customerApiService.getCreditProjection());
      } catch (projErr) {
        console.error('Failed to load credit projection:', projErr);
        setProjection(null);
      }
    } catch (err: any) {
      console.error('Failed to load customer account:', err);
      if (!silent) {
        setError(t('customer.account.loadError', 'No se pudo cargar tu cuenta. Inténtalo de nuevo en unos minutos.'));
      }
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  // Lanza el upgrade de un empleado a la última versión y recarga (silencioso) al terminar.
  const handleUpgradeEmployee = useCallback(async (employeeId: number) => {
    setUpgradingEmployeeId(employeeId);
    try {
      await customerApiService.upgradeMyEmployee(employeeId);
      await loadAll(true);
    } catch (err: any) {
      setError(err?.detail || t('customer.account.upgradeError', 'No se pudo actualizar el empleado.'));
    } finally {
      setUpgradingEmployeeId(null);
    }
  }, [loadAll, t]);

  // Refresco silencioso mientras algún empleado sigue desplegándose.
  useEffect(() => {
    const hasInProgress = employees.some(
      (e) => e.status === 'pending' || e.status === 'provisioning'
    );
    if (hasInProgress && pollTimer.current === null) {
      pollTimer.current = window.setInterval(() => loadAll(true), 8000);
    }
    if (!hasInProgress && pollTimer.current !== null) {
      window.clearInterval(pollTimer.current);
      pollTimer.current = null;
    }
    return () => {
      if (pollTimer.current !== null) {
        window.clearInterval(pollTimer.current);
        pollTimer.current = null;
      }
    };
  }, [employees, loadAll]);

  const isValidEmail = (email: string) => /\S+@\S+\.\S+/.test(email);

  const openAddUserDialog = () => {
    setNewUserEmail('');
    setNewUserName('');
    setNewUserAuthMethod('password');
    setEmailCheck(null);
    setAddUserError(null);
    setAddUserDialogOpen(true);
  };

  // Comprobación del email al perder el foco: si ese usuario YA existe en el
  // tenant, el alta solo lo añade a esta cuenta y a su proyecto (ni usuario
  // nuevo ni contraseña) — hay que decirlo ANTES de crear. Best-effort: el
  // servicio devuelve checked=false si no se pudo preguntar y no se avisa nada.
  const handleCheckEmail = async () => {
    const email = newUserEmail.trim().toLowerCase();
    if (!isValidEmail(email)) {
      setEmailCheck(null);
      return;
    }
    if (emailCheck && emailCheck.email === email) return;
    try {
      setCheckingEmail(true);
      setEmailCheck(await customerApiService.checkAccountUserEmail(email));
    } finally {
      setCheckingEmail(false);
    }
  };

  const handleAddUser = async () => {
    try {
      setAddUserLoading(true);
      setAddUserError(null);
      const response = await customerApiService.addAccountUser({
        email: newUserEmail.trim(),
        full_name: newUserName.trim(),
        auth_method: newUserAuthMethod
      });
      setAddUserDialogOpen(false);
      setNewUserEmail('');
      setNewUserName('');
      setEmailCheck(null);
      setUserCreatedNotice(null);
      if (response.generated_password) {
        // Contraseña de un solo uso: SOLO llega cuando el tenant la genera
        // (alta con contraseña de un usuario que no existía).
        setGeneratedPassword({
          email: response.user.email,
          password: response.generated_password
        });
      } else if (response.created === false) {
        setUserCreatedNotice(t(
          'customer.account.userExistedNotice',
          '{{email}} ya existía: se ha añadido a tu cuenta y a tu proyecto con sus credenciales de siempre (no se genera contraseña nueva).',
          { email: response.user.email }
        ));
      } else if (newUserAuthMethod === 'google') {
        setUserCreatedNotice(t(
          'customer.account.googleOnlyNotice',
          '{{email}} entrará con su cuenta de Google: no hay contraseña que compartir.',
          { email: response.user.email }
        ));
      }
      // Los usuarios primero (rápido, es lo que acaba de cambiar) y el resto
      // del panel después: el contador de usuarios de la cabecera va en /account.
      await reloadUsers();
      loadAll(true);
    } catch (err: any) {
      // El diálogo NO se cierra: el motivo real (email ya en la cuenta, fallo
      // del tenant…) se enseña aquí con los datos escritos aún puestos.
      console.error('Failed to add account user:', err);
      setAddUserError(err?.detail || t('customer.account.addUserError', 'No se pudo añadir el usuario'));
    } finally {
      setAddUserLoading(false);
    }
  };

  // Baja de empleado: retira su app del tenant y libera cupo. `tenant_error`
  // = la baja se completó pero la app no pudo retirarse (se avisa, el cupo
  // queda libre igualmente).
  const handleRemoveEmployee = async () => {
    if (!removeTarget) return;
    try {
      setRemoveLoading(true);
      const res = await customerApiService.removeEmployee(removeTarget.id);
      if (res?.tenant_error) {
        toast.warning(t(
          'customer.account.removeEmployeePartial',
          'Empleado retirado de tu cuenta (cupo liberado), pero su aplicación no pudo eliminarse: {{error}}',
          { error: res.tenant_error }
        ));
      } else {
        toast.success(t('customer.account.employeeRemoved', 'Empleado eliminado'));
      }
      loadAll(true);
    } catch (err: any) {
      console.error('Failed to remove employee:', err);
      toast.error(err?.detail || t('customer.account.removeEmployeeError', 'No se pudo eliminar el empleado'));
    } finally {
      setRemoveLoading(false);
      setRemoveTarget(null);
    }
  };

  // Refresco manual del consumo: el sync del GET va con throttle de 5 min, así
  // que sin este botón el cliente no tenía forma de forzar el dato al día.
  const handleRefreshConsumption = async () => {
    try {
      setRefreshingConsumption(true);
      const res = await customerApiService.refreshConsumption();
      if (res && res.synced === false) {
        toast.warning(t(
          'customer.account.consumptionStale',
          'No se ha podido contactar con el servicio; se muestra el último consumo conocido.'
        ));
      }
      await loadAll(true);
    } catch (err: any) {
      console.error('Failed to refresh consumption:', err);
      toast.error(err?.detail || t('customer.account.consumptionRefreshError', 'No se pudo actualizar el consumo'));
    } finally {
      setRefreshingConsumption(false);
    }
  };

  const openAssignDialog = (user: AccountUser) => {
    setUserToAssign(user);
    setAssignEmployeeId('');
    setAssignRole('member');
    setAssignError(null);
    setAssignDialogOpen(true);
  };

  const handleAssignUser = async () => {
    if (!userToAssign || assignEmployeeId === '') return;
    try {
      setAssignLoading(true);
      setAssignError(null);
      await customerApiService.assignAccountUser(userToAssign.id, {
        account_employee_id: assignEmployeeId as number,
        role: assignRole
      });
      setAssignDialogOpen(false);
      setUserToAssign(null);
      // ESPERADO antes de seguir: la columna «Empleados asignados» tiene que
      // reflejar el cambio al cerrar el diálogo, no cuando termine el refresco
      // completo (que habla con el tenant y tarda segundos).
      await reloadUsers();
      loadAll(true);
    } catch (err: any) {
      // Inline y con el diálogo ABIERTO: el 409 del backend trae el motivo
      // concreto (ya asignado y con qué rol, empleado no activo…) y desde aquí
      // se puede cambiar de empleado o de rol sin volver a empezar.
      console.error('Failed to assign user:', err);
      setAssignError(err?.detail || t('customer.account.assignError', 'No se pudo asignar el usuario al empleado'));
    } finally {
      setAssignLoading(false);
    }
  };

  const handleUnassignUser = async () => {
    if (!unassignTarget) return;
    try {
      setUnassignLoading(true);
      setUnassignError(null);
      await customerApiService.unassignUser(
        unassignTarget.user.id,
        unassignTarget.assignment.account_employee_id
      );
      setUnassignTarget(null);
      toast.success(t('customer.account.unassigned', 'Asignación quitada'));
      await reloadUsers();
      loadAll(true);
    } catch (err: any) {
      // La confirmación se queda abierta con el motivo real (p. ej. la app
      // rechazó la baja): cerrarla y sacar un toast escondía por qué el chip
      // seguía ahí.
      console.error('Failed to unassign user:', err);
      setUnassignError(err?.detail || t('customer.account.unassignError', 'No se pudo quitar la asignación'));
    } finally {
      setUnassignLoading(false);
    }
  };

  // ── Quién gestiona la cuenta ─────────────────────────────────────────────
  // El partner (si lo hay) es el interlocutor REAL del cliente para créditos,
  // cupo y facturación: además del bloque fijo de arriba, los avisos que antes
  // decían «tu proveedor» le nombran, que es lo accionable.
  const managedBy = account?.managed_by_reseller ?? null;

  // ── Cupo de empleados ────────────────────────────────────────────────────
  // employees_count viene del backend (autoridad); fallback local sin «removed».
  // max_employees puede faltar si el backend aún no lo devuelve: en ese caso no
  // pintamos cupo y dejamos contratar (el backend responde 409 si no procede).
  const usedSlots = account?.employees_count ?? employees.filter((e) => e.status !== 'removed').length;
  const maxEmployees = typeof account?.max_employees === 'number' ? account.max_employees : null;
  const quotaFull = maxEmployees !== null && usedSlots >= maxEmployees;

  // Campos «dos bolsas» combinados. El backend los devuelve ANIDADOS en
  // `credit_summary` (summary_numbers → CreditSummaryOut), no al nivel superior:
  // sin leer ese objeto, la tarjeta caería al saldo simple y no se verían las
  // tres cifras (Plan / Extra / Total). Se cubren las dos formas por si el
  // backend evoluciona a top-level, y el más reciente (credits) pisa al summary.
  const planData: CreditPlanFields = {
    ...(account ?? {}),
    ...(account?.credit_summary ?? {}),
    ...(creditsInfo ?? {}),
    ...(creditsInfo?.credit_summary ?? {})
  };

  // Modelo «una bolsa, límites por empleado»: la asignación es un LÍMITE de
  // gasto, no una transferencia — no hay «disponible para repartir» y la suma
  // de límites puede superar el saldo, así que aquí ya no se calcula tope.
  // Aviso ámbar cuando el disponible se agota: ≤ 10 % del total del ciclo
  // (plan concedido + bolsa extra) o directamente ≤ 0.
  const availableNow =
    toNum(planData.available_total) ?? toNum(creditBalance) ?? 0;
  const cycleTotal = (toNum(planData.plan_total) ?? 0) + (toNum(planData.extra_balance) ?? 0);
  const lowBalance =
    availableNow <= 0 || (cycleTotal > 0 && availableNow <= cycleTotal * 0.1);

  // ¿Hay plan con ciclo abierto? Manda la fecha de renovación (solo existe
  // con ciclo vigente); consumed_cycle ≠ null la confirma en backends nuevos.
  const hasPlanCycle =
    planData.plan_renews_at != null || toNum(account?.consumed_cycle) !== null;

  // ── Almacenamiento de la cuenta ──────────────────────────────────────────
  // Todo derivado del backend (usado/incluido/facturable + el tramo y el tope
  // por volumen): el frontend NO duplica ninguna constante — si el marketplace
  // cambia el tramo o el precio, la UI lo refleja sin tocar código.
  const storage: AccountStorage | null = account?.storage ?? null;
  const storageStep = storage?.step_gib ?? 20;
  const storageMaxVolume = storage?.max_volume_gib ?? 100;
  const storagePricePerGib = toNum(storage?.credits_per_gib_cycle) ?? 0;

  // Créditos por ciclo que añadiría ocupar `extraGib` GiB más: solo cuenta lo
  // que REBASA lo incluido — los primeros GiB pueden ser gratis y decir lo
  // contrario asustaría sin motivo.
  const storageExtraCost = (extraGib: number): number => {
    if (!storage || storagePricePerGib <= 0 || extraGib <= 0) return 0;
    const before = Math.max(0, storage.used_gib - storage.included_gib);
    const after = Math.max(0, storage.used_gib + extraGib - storage.included_gib);
    return (after - before) * storagePricePerGib;
  };

  // Impacto EN VIVO del tamaño elegido para el empleado que se va a contratar
  // (lo pinta el picker, y aquí sirve para bloquear el botón si se pasa del
  // techo de la cuenta — el backend respondería 409 con el mismo motivo).
  const hireImpact = storageImpact(storage, hireVolumes);

  const hireCreditsValid =
    hireCredits.trim() === '' || (!Number.isNaN(Number(hireCredits)) && Number(hireCredits) >= 0);
  const allocateValid =
    allocateValue.trim() !== '' && !Number.isNaN(Number(allocateValue)) && Number(allocateValue) >= 0;

  // Usuarios elegibles como administrador: los de la cuenta que no estén
  // deshabilitados (uno pendiente de crear su contraseña sirve: el alta en la
  // plataforma se reintenta al provisionar).
  const adminCandidates = users.filter((u) => u.status !== 'disabled');

  const openHireDialog = async () => {
    setHireDialogOpen(true);
    setHireError(null);
    setHireListingSlug('');
    setHireDisplayName('');
    setHireCredits('');
    // Tamaño de salida = los mínimos del backend (lo que aplicaría si no se
    // mandara nada): contratar sin tocar nada tiene que costar lo de siempre.
    setHireVolumes(defaultVolumes(storage));
    // Con un único usuario elegible se preselecciona: es la única opción.
    setHireAdminUserId(adminCandidates.length === 1 ? adminCandidates[0].id : '');
    try {
      setLoadingListings(true);
      const options = await apiService.getPublicListingOptions();
      setListingOptions(options);
    } catch (err) {
      console.error('Failed to load listing catalog:', err);
      setHireError(t('customer.account.catalogError', 'No se pudo cargar el catálogo de empleados'));
    } finally {
      setLoadingListings(false);
    }
  };

  const handleHireEmployee = async () => {
    if (!hireListingSlug || !hireCreditsValid || hireAdminUserId === '') return;
    try {
      setHireLoading(true);
      setHireError(null);
      const payload: CustomerHireEmployeeRequest = {
        listing_slug: hireListingSlug,
        admin_account_user_id: hireAdminUserId as number,
        // Los volúmenes VIAJAN siempre (aunque sean los mínimos): el empleado
        // nace con el disco elegido — el install del tenant los recibe desde
        // estas mismas columnas, no hay un segundo paso que ampliarlos después.
        workspace_gib: hireVolumes.workspace_gib,
        data_gib: hireVolumes.data_gib,
        // CAPACIDAD: viaja igual y por lo mismo — el empleado nace con ella.
        size: hireSize
      };
      if (hireDisplayName.trim()) payload.display_name = hireDisplayName.trim();
      if (hireCredits.trim() !== '' && Number(hireCredits) > 0) {
        payload.allocated_credits = Number(hireCredits);
      }
      await customerApiService.hireEmployee(payload);
      setHireDialogOpen(false);
      // La tarjeta nueva aparece en provisioning; el sondeo silencioso ya existente
      // la refresca sola hasta que pase a active.
      loadAll(true);
    } catch (err: any) {
      console.error('Failed to hire employee:', err);
      // 409 = cupo superado / sin saldo / reparto excede saldo → el detail del
      // backend es el mensaje que enseña la UI (se queda dentro del diálogo).
      setHireError(err?.detail || t('customer.account.hireError', 'No se pudo contratar el empleado'));
    } finally {
      setHireLoading(false);
    }
  };

  const openStorageDialog = (emp: AccountEmployee) => {
    setStorageTarget(emp);
    setStorageSplit('both');
    setStorageError(null);
  };

  const handleExpandStorage = async () => {
    if (!storageTarget) return;
    const target = nextVolumes(storageTarget, storageSplit, storageStep);
    try {
      setStorageLoading(true);
      setStorageError(null);
      const res = await customerApiService.expandEmployeeStorage(storageTarget.id, target);
      // Un 2xx NO quiere decir que el disco haya crecido: el backend empuja la
      // ampliación al tenant y devuelve en `sync` lo que pasó de verdad. Si no
      // se aplicó, el diálogo se queda abierto con el motivo — cerrarlo con un
      // «ampliado» sería mentir sobre un espacio que el cliente no tiene.
      const feedback = storageSyncFeedback(res?.sync, t);
      if (feedback.failed) {
        setStorageError(feedback.message);
        loadAll(true);
        return;
      }
      setStorageTarget(null);
      if (feedback.severity === 'warning') {
        toast.warning(feedback.message);
      } else {
        toast.success(feedback.message);
      }
      loadAll(true);
    } catch (err: any) {
      // 409 con el motivo real (tramo, tope por volumen, techo de la cuenta o
      // de tu partner, saldo insuficiente): se queda DENTRO del diálogo para
      // poder elegir otro reparto sin volver a empezar.
      console.error('Failed to expand storage:', err);
      setStorageError(
        err?.detail || t('customer.account.storageError', 'No se pudo ampliar el almacenamiento')
      );
    } finally {
      setStorageLoading(false);
    }
  };

  const openAllocateDialog = (emp: AccountEmployee) => {
    setEmployeeToAllocate(emp);
    setAllocateValue(
      emp.allocated_credits === null || emp.allocated_credits === undefined
        ? ''
        : String(Number(emp.allocated_credits))
    );
    setAllocateError(null);
    setAllocateDialogOpen(true);
  };

  const handleAllocateCredits = async () => {
    if (!employeeToAllocate || !allocateValid) return;
    try {
      setAllocateLoading(true);
      setAllocateError(null);
      await customerApiService.allocateCredits(employeeToAllocate.id, Number(allocateValue));
      setAllocateDialogOpen(false);
      setEmployeeToAllocate(null);
      loadAll(true);
    } catch (err: any) {
      console.error('Failed to allocate credits:', err);
      setAllocateError(err?.detail || t('customer.account.allocateError', 'No se pudieron asignar los créditos'));
    } finally {
      setAllocateLoading(false);
    }
  };

  // Selector de periodo de la sección Créditos: refetch de /account/credits
  // con ?period — el backend filtra los asientos y devuelve consumed_period.
  const handleCreditsPeriodChange = async (
    _event: React.MouseEvent<HTMLElement>,
    value: CreditsPeriod | null
  ) => {
    if (!value) return;
    setCreditsPeriod(value);
    creditsPeriodRef.current = value;
    try {
      const data = await customerApiService.getMyAccountCredits(value);
      setCreditBalance(data.balance);
      setCreditEntries(data.entries);
      setCreditsInfo(data);
    } catch (err) {
      console.error('Failed to load credits for period:', err);
    }
  };

  // Etiqueta del consumo según el periodo EFECTIVO (el backend puede degradar
  // cycle→all si la cuenta no tiene plan). Backend viejo sin periodo → «Total».
  const periodConsumedLabel = (period: string | undefined): string => {
    switch (period) {
      case 'cycle':
        return t('customer.account.consumedCycle', 'Consumido este ciclo');
      case '30d':
        return t('customer.account.consumed30d', 'Consumido (últimos 30 días)');
      default:
        return t('customer.account.consumedTotalLabel', 'Consumido (total)');
    }
  };

  // «actualizado hace X» del consumo. Sin dato (proceso del backend recién
  // arrancado) devuelve null y solo se ofrece el botón de actualizar: preferimos
  // no decir nada a inventar una antigüedad.
  const syncedAgo = (iso?: string | null): string | null => {
    if (!iso) return null;
    const utc = iso.endsWith('Z') ? iso : `${iso}Z`;
    const ms = Date.now() - new Date(utc).getTime();
    if (Number.isNaN(ms) || ms < 0) return null;
    const minutes = Math.floor(ms / 60000);
    if (minutes < 1) return t('customer.account.agoJustNow', 'hace un momento');
    if (minutes < 60) return t('customer.account.agoMinutes', 'hace {{n}} min', { n: minutes });
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return t('customer.account.agoHours', 'hace {{n}} h', { n: hours });
    return t('customer.account.agoDays', 'hace {{n}} d', { n: Math.floor(hours / 24) });
  };

  // La frescura la emiten los DOS endpoints (/account y /account/credits): se
  // usa la del último que respondió, y si ninguno la trae no se pinta.
  const consumptionSyncedAt =
    creditsInfo?.consumption_synced_at ?? account?.consumption_synced_at ?? null;
  const consumptionSyncFailed =
    creditsInfo?.consumption_sync_failed ?? account?.consumption_sync_failed ?? false;
  const consumptionAgo = syncedAgo(consumptionSyncedAt);

  const employeeName = (employeeId: number | null): string => {
    if (employeeId === null) return '—';
    const emp = employees.find((e) => e.id === employeeId);
    return emp ? (emp.display_name || emp.listing_slug) : `#${employeeId}`;
  };

  const assignableEmployees = employees.filter((e) => e.status === 'active');

  if (loading) {
    return (
      <CustomerLayout>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
          <CircularProgress />
        </Box>
      </CustomerLayout>
    );
  }

  // Estado vacío amable: el usuario no tiene cuenta de empleados IA.
  if (!hasAccount) {
    return (
      <CustomerLayout>
        <Container maxWidth="md" sx={{ py: 6 }}>
          <Card elevation={0} sx={{ border: '1px solid #e0e0e0', borderRadius: 2, textAlign: 'center', py: 6, px: 3 }}>
            <SmartToyIcon sx={{ fontSize: 56, color: 'text.disabled', mb: 2 }} />
            <Typography variant="h6" gutterBottom>
              {t('customer.account.noAccountTitle', 'Todavía no tienes una cuenta de empleados IA')}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('customer.account.noAccountMessage', 'Cuando contrates tu primer empleado IA, aquí verás tu saldo, tus empleados y tus usuarios. Si crees que deberías tener acceso, contacta con tu proveedor.')}
            </Typography>
          </Card>
        </Container>
      </CustomerLayout>
    );
  }

  return (
    <CustomerLayout>
      <Container maxWidth="lg" sx={{ py: 6 }}>
        {/* Header */}
        <Box mb={4}>
          <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
            {t('customer.account.title', 'Mi cuenta')}
          </Typography>
          <Typography variant="body1" color="text.secondary">
            {account?.name}
            {account?.company ? ` · ${account.company}` : ''}
          </Typography>
          {/* Ayuda contextual: el portal maneja conceptos que nadie explicaba
              (dos bolsas de créditos, límite ≠ transferencia, disco por
              empleado que solo crece). Aquí arriba porque es donde mira quien
              entra por primera vez; cada bloque enlaza a su capítulo de la guía. */}
          <AccountHelpPanel sx={{ mt: 1.5, ml: -1 }} />
        </Box>

        {/* Quién gestiona la cuenta: el banner del layout lo dice de pasada,
            pero el cliente lo BUSCA aquí, en su cuenta, cuando necesita más
            créditos, más cupo o ayuda. Por eso es un bloque permanente y con
            acción (mailto), no una línea suelta bajo el título. También explica
            por qué no hay planes ni facturación en este portal — el silencio
            se leía como una carencia del producto. */}
        {managedBy && (
          <Card
            elevation={0}
            sx={{
              mb: 3,
              border: '1px solid',
              borderColor: 'primary.light',
              bgcolor: '#f4f9ff',
              borderRadius: 2
            }}
          >
            <CardContent>
              <Box display="flex" alignItems="flex-start" gap={1.5}>
                <HandshakeIcon color="primary" />
                <Box flexGrow={1}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                    {t('customer.account.managedBy', 'Tu cuenta la gestiona {{name}}', {
                      name: managedBy.name
                    })}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    {t(
                      'customer.account.managedByHelp',
                      'Tus créditos, tu cupo de empleados y cualquier gestión administrativa los lleva {{name}}. Escríbeles cuando necesites ampliar o tengas cualquier duda.',
                      { name: managedBy.name }
                    )}
                  </Typography>
                  {managedBy.contact_email && (
                    <Typography variant="body2" sx={{ mt: 1 }}>
                      <MuiLink href={`mailto:${managedBy.contact_email}`} underline="hover">
                        {managedBy.contact_email}
                      </MuiLink>
                    </Typography>
                  )}
                </Box>
              </Box>
            </CardContent>
          </Card>
        )}

        {error && <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>{error}</Alert>}

        {/* Aviso ámbar de saldo: disponible ≤ 10% del total del ciclo o ≤ 0.
            Con límites por empleado el saldo solo baja con el consumo real,
            así que este es EL aviso operativo de la cuenta. */}
        {lowBalance && (
          <Alert severity="warning" sx={{ mb: 3 }}>
            {managedBy
              ? t(
                  'customer.account.lowBalanceWarningManaged',
                  'Saldo casi agotado: pide más créditos a {{name}} para que tus empleados IA no dejen de trabajar. Tu saldo solo baja con el consumo real.',
                  { name: managedBy.name }
                )
              : t('customer.account.lowBalanceWarning', 'Saldo casi agotado: recarga o contacta con tu proveedor para que tus empleados IA no dejen de trabajar. Tu saldo solo baja con el consumo real.')}
          </Alert>
        )}

        {/* Contraseña generada — UNA sola vez */}
        {generatedPassword && (
          <Alert
            severity="warning"
            sx={{ mb: 3 }}
            onClose={() => setGeneratedPassword(null)}
            action={
              <IconButton
                size="small"
                onClick={() => navigator.clipboard.writeText(generatedPassword.password)}
                title={t('customer.account.copyPassword', 'Copiar contraseña')}
              >
                <ContentCopyIcon fontSize="small" />
              </IconButton>
            }
          >
            <AlertTitle>{t('customer.account.passwordOnceTitle', 'Guarda esta contraseña: no volverá a mostrarse')}</AlertTitle>
            {t('customer.account.passwordFor', 'Contraseña para')} <strong>{generatedPassword.email}</strong>:{' '}
            <Typography component="span" sx={{ fontFamily: 'monospace', fontWeight: 700 }}>
              {generatedPassword.password}
            </Typography>
            <Typography variant="body2" sx={{ mt: 0.5 }}>
              {t('customer.account.passwordEmailed', 'También se le ha enviado por correo para que la cambie al entrar.')}
            </Typography>
          </Alert>
        )}

        {/* Alta SIN contraseña que enseñar: usuario que ya existía en el
            tenant o alta solo-Google. Sin este aviso el cliente se quedaba
            esperando una contraseña que nunca iba a aparecer. */}
        {userCreatedNotice && (
          <Alert severity="info" sx={{ mb: 3 }} onClose={() => setUserCreatedNotice(null)}>
            {userCreatedNotice}
          </Alert>
        )}

        {/* Resumen — tarjetas compactas y homogéneas. La de Créditos lleva
            SOLO la cifra operativa (disponible): el resumen completo «dos
            bolsas» vive en la única sección «Créditos» de abajo, con su
            selector de periodo y el ledger (antes estaba duplicado). La de
            Almacenamiento solo aparece si el backend lo informa: sin el dato
            preferimos no pintar una tarjeta a ceros que parezca real. */}
        <Grid container spacing={3} mb={4}>
          <Grid item xs={12} sm={storage ? 3 : 4}>
            <Card elevation={0} sx={{ border: '1px solid #e0e0e0', borderRadius: 2, height: '100%' }}>
              <CardContent>
                <Box display="flex" alignItems="center" gap={1} mb={1}>
                  <WalletIcon color="primary" fontSize="small" />
                  <Typography variant="body2" color="text.secondary">
                    {t('customer.account.credits', 'Créditos')}
                  </Typography>
                </Box>
                <Typography variant="h5" fontWeight={700}>
                  {formatCredits(availableNow)}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {t('customer.account.availableCaption', 'disponibles')}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={storage ? 3 : 4}>
            <Card elevation={0} sx={{ border: '1px solid #e0e0e0', borderRadius: 2, height: '100%' }}>
              <CardContent>
                <Box display="flex" alignItems="center" gap={1} mb={1}>
                  <SmartToyIcon color="action" fontSize="small" />
                  <Typography variant="body2" color="text.secondary">
                    {t('customer.account.employeesCount', 'Empleados IA')}
                  </Typography>
                </Box>
                <Typography variant="h5" fontWeight={700}>
                  {usedSlots}
                  {maxEmployees !== null ? ` / ${maxEmployees}` : ''}
                </Typography>
                {maxEmployees !== null && (
                  <Typography variant="caption" color="text.secondary">
                    {t('customer.account.quotaCaption', 'cupo contratado')}
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={storage ? 3 : 4}>
            <Card elevation={0} sx={{ border: '1px solid #e0e0e0', borderRadius: 2, height: '100%' }}>
              <CardContent>
                <Box display="flex" alignItems="center" gap={1} mb={1}>
                  <PeopleIcon color="action" fontSize="small" />
                  <Typography variant="body2" color="text.secondary">
                    {t('customer.account.usersCount', 'Usuarios')}
                  </Typography>
                </Box>
                <Typography variant="h5" fontWeight={700}>
                  {account?.users_count ?? users.length}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          {/* Almacenamiento de la cuenta: usado / incluido sin coste, y el
              cargo por ciclo si se pasa. Está arriba con el resto de métricas
              porque es una dimensión más de lo contratado — no una letra
              pequeña que aparece cuando llega el cargo. */}
          {storage && (
            <Grid item xs={12} sm={3}>
              <Card elevation={0} sx={{ border: '1px solid #e0e0e0', borderRadius: 2, height: '100%' }}>
                <CardContent>
                  <Box display="flex" alignItems="center" gap={1} mb={1}>
                    <StorageIcon color="action" fontSize="small" />
                    <Typography variant="body2" color="text.secondary">
                      {t('customer.account.storage', 'Almacenamiento')}
                    </Typography>
                  </Box>
                  {/* CUENTA SIN EMPLEADOS: un contador a «0 GiB» no explica
                      nada y deja al cliente buscando un control para pedir más
                      espacio que NO existe a nivel de cuenta. El motivo es de
                      diseño: el disco pertenece a cada EMPLEADO (se elige al
                      contratarlo y se amplía desde su tarjeta), así que aquí el
                      protagonista es esa explicación, con la acción que
                      realmente desbloquea el asunto. */}
                  {usedSlots === 0 ? (
                    <>
                      <Typography variant="body2" color="text.secondary">
                        {managedBy
                          ? t(
                              'customer.account.storageNoEmployeesManaged',
                              'El espacio va con cada empleado: lo eliges al contratarlo. Tu espacio lo gestiona {{name}}, pídeselo a ellos si necesitas más.',
                              { name: managedBy.name }
                            )
                          : t(
                              'customer.account.storageNoEmployees',
                              'El espacio va con cada empleado: lo eliges al contratarlo y puedes ampliarlo después desde su tarjeta.'
                            )}
                      </Typography>
                      {!quotaFull && (
                        <Button
                          size="small"
                          startIcon={<AddIcon />}
                          onClick={openHireDialog}
                          disabled={readOnly}
                          sx={{ textTransform: 'none', mt: 1, px: 0 }}
                        >
                          {t('customer.account.hireEmployee', 'Contratar empleado')}
                        </Button>
                      )}
                    </>
                  ) : (
                    <>
                      <Typography variant="h5" fontWeight={700}>
                        {storage.used_gib}
                        {storage.max_gib > 0 ? ` / ${storage.max_gib}` : ''} GiB
                      </Typography>
                      <Typography variant="caption" color="text.secondary" display="block">
                        {t('customer.account.storageIncludedCaption', '{{included}} GiB incluidos', {
                          included: storage.included_gib
                        })}
                      </Typography>
                      {/* Dónde se toca el espacio. Va SIEMPRE (no solo con la
                          cuenta vacía): con empleados ya contratados, la
                          pregunta deja de ser «dónde está el control» y pasa a
                          ser «en cuál de ellos», y esta línea la contesta. */}
                      <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                        {t(
                          'customer.account.storagePerEmployeeHint',
                          'Se asigna por empleado: amplíalo desde la tarjeta del empleado.'
                        )}
                      </Typography>
                      {/* Cuenta GESTIONADA: aquí no se habla de coste ni se
                          insinúa (su almacenamiento lo lleva su partner, que es
                          quien lo paga y quien decide su espacio). Lo accionable es
                          a quién pedir más sitio, no cuánto costaría. */}
                      {storage.billed_to_partner && managedBy && (
                        <Typography variant="caption" color="text.secondary" display="block">
                          {t(
                            'customer.account.storageManagedCaption',
                            'Tu espacio lo gestiona {{name}}: pídeselo a ellos si necesitas más.',
                            { name: managedBy.name }
                          )}
                        </Typography>
                      )}
                      {!storage.billed_to_partner
                        && storage.billable_gib > 0 && toNum(storage.cycle_credits) !== 0 && (
                        <Typography variant="caption" color="warning.main" display="block">
                          {t(
                            'customer.account.storageBillableCaption',
                            '{{gib}} GiB de más · {{credits}} créditos/ciclo',
                            {
                              gib: storage.billable_gib,
                              credits: formatCredits(storage.cycle_credits)
                            }
                          )}
                        </Typography>
                      )}
                    </>
                  )}
                </CardContent>
              </Card>
            </Grid>
          )}
        </Grid>

        {/* Mis empleados */}
        <Box mb={4}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2} flexWrap="wrap" gap={1}>
            <Box display="flex" alignItems="baseline" gap={1.5}>
              <Typography variant="h5" sx={{ fontWeight: 700 }}>
                {t('customer.account.myEmployees', 'Mis empleados')}
              </Typography>
              {maxEmployees !== null && (
                <Typography variant="body2" color="text.secondary">
                  {t('customer.account.quota', 'Cupo: {{used}}/{{max}}', { used: usedSlots, max: maxEmployees })}
                </Typography>
              )}
            </Box>
            {/* Contratar solo con cupo libre; sin dato de cupo, el backend decide (409). */}
            {!quotaFull ? (
              <Button
                variant="contained"
                size="small"
                startIcon={<AddIcon />}
                onClick={openHireDialog}
                disabled={readOnly}
                sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 2, boxShadow: 'none' }}
              >
                {t('customer.account.hireEmployee', 'Contratar empleado')}
              </Button>
            ) : (
              <Typography variant="caption" color="text.secondary">
                {managedBy
                  ? t(
                      'customer.account.quotaFullHintManaged',
                      'Has alcanzado el cupo de empleados de tu cuenta. Pídele a {{name}} que lo amplíe.',
                      { name: managedBy.name }
                    )
                  : t('customer.account.quotaFullHint', 'Has alcanzado el cupo de empleados de tu cuenta. Contacta con tu proveedor para ampliarlo.')}
              </Typography>
            )}
          </Box>
          {/* Aviso de actualización: si algún empleado desplegado tiene una versión
              superior publicada en el marketplace. El upgrade se ofrecerá aparte. */}
          {employeeUpdates.some((u) => u.update_available) && (
            <Alert severity="info" sx={{ mb: 2 }}>
              <AlertTitle>
                {t('customer.account.updatesTitle', 'Hay nuevas versiones disponibles en el marketplace')}
              </AlertTitle>
              {employeeUpdates
                .filter((u) => u.update_available)
                .map((u) => (
                  <Box key={u.employee_id} sx={{ fontSize: 14, display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                    <span>
                      <strong>{u.display_name || u.listing_slug}</strong>:{' '}
                      {t('customer.account.updateFromTo', 'de {{from}} a {{to}}', {
                        from: u.installed_version || '—',
                        to: u.latest_version || '—'
                      })}
                    </span>
                    <Button
                      size="small"
                      variant="outlined"
                      disabled={readOnly || upgradingEmployeeId === u.employee_id}
                      onClick={() => handleUpgradeEmployee(u.employee_id)}
                      sx={{ textTransform: 'none', ml: 'auto' }}
                    >
                      {upgradingEmployeeId === u.employee_id
                        ? t('customer.account.upgrading', 'Actualizando…')
                        : t('customer.account.upgrade', 'Actualizar')}
                    </Button>
                  </Box>
                ))}
            </Alert>
          )}
          {employees.length === 0 ? (
            <Card elevation={0} sx={{ border: '1px solid #e0e0e0', borderRadius: 2, textAlign: 'center', py: 4 }}>
              <Typography variant="body2" color="text.secondary">
                {t('customer.account.noEmployees', 'Todavía no tienes empleados IA en tu cuenta.')}
              </Typography>
            </Card>
          ) : (
            <Grid container spacing={3}>
              {employees.map((emp) => (
                <Grid item xs={12} sm={6} md={4} key={emp.id}>
                  <Card
                    elevation={0}
                    sx={{
                      border: '1px solid #e0e0e0',
                      borderRadius: 2,
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column'
                    }}
                  >
                    <CardContent sx={{ flexGrow: 1 }}>
                      <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1.5}>
                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                          {emp.display_name || emp.listing_slug}
                        </Typography>
                        <EmployeeStatusChip status={emp.status} />
                      </Box>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        {emp.listing_slug}
                        {emp.listing_version ? ` · v${emp.listing_version}` : ''}
                      </Typography>
                      {/* Administrador: usuario de la cuenta que lo administra y
                          cuya identidad usa el empleado para trabajar. Los
                          empleados anteriores a este modelo no lo tienen: se
                          dice, no se deja el hueco. */}
                      <Typography variant="body2" color="text.secondary">
                        {t('customer.account.employeeAdmin', 'Administrador')}:{' '}
                        <Typography component="span" variant="body2" fontWeight={600} color="text.primary">
                          {emp.admin_user_name
                            || t('customer.account.noAdminDeclared', 'Sin administrador declarado')}
                        </Typography>
                      </Typography>
                      {emp.allocated_credits !== null && emp.allocated_credits !== undefined && (
                        <Typography variant="body2" color="text.secondary">
                          {t('customer.account.allocatedCredits', 'Límite mensual')}:{' '}
                          <Typography component="span" variant="body2" fontWeight={600} color="text.primary">
                            {toNum(emp.allocated_credits) === 0
                              ? t('customer.account.noLimit', 'Sin límite')
                              : formatCredits(emp.allocated_credits)}
                          </Typography>
                        </Typography>
                      )}
                      {/* Almacenamiento del empleado: sus dos volúmenes
                          reales. Se pinta SIEMPRE (no solo cuando se ha
                          ampliado) porque es lo que ocupa de la cuenta desde
                          el primer día. */}
                      <Typography variant="body2" color="text.secondary">
                        {t('customer.account.storage', 'Almacenamiento')}:{' '}
                        <Typography component="span" variant="body2" fontWeight={600} color="text.primary">
                          {employeeVolumes(emp).ws + employeeVolumes(emp).data} GiB
                        </Typography>
                        <Typography component="span" variant="body2" color="text.secondary">
                          {' '}
                          {t('customer.account.storageBreakdown', '({{ws}} espacio de trabajo + {{data}} datos)', {
                            ws: employeeVolumes(emp).ws,
                            data: employeeVolumes(emp).data
                          })}
                        </Typography>
                      </Typography>
                      {/* CAPACIDAD con la que trabaja. Se pinta siempre, igual
                          que el disco: «no pone nada» no se lee como «es la
                          estándar», se lee como «no lo sé». */}
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                        <Typography variant="body2" color="text.secondary">
                          {t('customer.account.capacity', 'Capacidad')}:
                        </Typography>
                        <EmployeeSizeChip size={emp.size} />
                      </Box>
                      <EmployeeConsumption emp={emp} />
                      {emp.status === 'failed' && emp.error_message && (
                        <Alert severity="error" sx={{ mt: 1 }}>
                          {emp.error_message}
                        </Alert>
                      )}
                      {(emp.status === 'pending' || emp.status === 'provisioning') && (
                        <Typography variant="caption" color="text.secondary" display="block" mt={1}>
                          {t('customer.account.provisioningHint', 'Tu empleado se está preparando. Puede tardar unos minutos.')}
                        </Typography>
                      )}
                    </CardContent>
                    <EmployeeActions
                      emp={emp}
                      readOnly={readOnly}
                      storageStep={storageStep}
                      onAllocate={openAllocateDialog}
                      onExpandStorage={openStorageDialog}
                      onRemove={setRemoveTarget}
                    />
                  </Card>
                </Grid>
              ))}
            </Grid>
          )}
        </Box>

        {/* Usuarios */}
        <Box mb={4}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h5" sx={{ fontWeight: 700 }}>
              {t('customer.account.users', 'Usuarios')}
            </Typography>
            <Button
              variant="contained"
              size="small"
              startIcon={<PersonAddIcon />}
              onClick={openAddUserDialog}
              disabled={readOnly}
              sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 2, boxShadow: 'none' }}
            >
              {t('customer.account.addUser', 'Añadir usuario')}
            </Button>
          </Box>
          {users.length === 0 ? (
            <Card elevation={0} sx={{ border: '1px solid #e0e0e0', borderRadius: 2, textAlign: 'center', py: 4 }}>
              <Typography variant="body2" color="text.secondary">
                {t('customer.account.noUsers', 'Añade usuarios para que tu equipo pueda usar los empleados IA.')}
              </Typography>
            </Card>
          ) : (
            <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid #e0e0e0', borderRadius: 2 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>{t('customer.account.fields.email', 'Email')}</TableCell>
                    <TableCell>{t('customer.account.fields.fullName', 'Nombre')}</TableCell>
                    <TableCell>{t('customer.account.fields.status', 'Estado')}</TableCell>
                    <TableCell>{t('customer.account.fields.assignedEmployees', 'Empleados asignados')}</TableCell>
                    <TableCell align="right">{t('customer.account.fields.actions', 'Acciones')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {users.map((user) => (
                    <TableRow key={user.id} hover>
                      <TableCell>{user.email}</TableCell>
                      <TableCell>{user.full_name}</TableCell>
                      <TableCell>
                        {/* Estado REAL en el tenant cuando el backend lo trae
                            («Pendiente de crear contraseña» / «Activo»); si no,
                            el estado local de siempre. */}
                        <AccountUserStatusChip user={user} />
                      </TableCell>
                      <TableCell>
                        {/* Chips «{empleado} · {rol}» desde assignments; el
                            backend ya excluye empleados removed. La X del chip
                            desasigna (con confirmación ligera); en modo
                            impersonación (readOnly) no se ofrece. */}
                        {(user.assignments?.length ?? 0) === 0 ? (
                          <Typography variant="caption" color="text.secondary">
                            {t('customer.account.noAssignments', 'Sin asignar')}
                          </Typography>
                        ) : (
                          <Box display="flex" gap={0.5} flexWrap="wrap">
                            {(user.assignments ?? []).map((a) => (
                              <Chip
                                key={`${a.account_employee_id}-${a.role}`}
                                size="small"
                                variant="outlined"
                                label={`${a.employee_name} · ${a.role === 'admin'
                                  ? t('customer.account.role.admin', 'Administrador')
                                  : t('customer.account.role.member', 'Miembro')}`}
                                onDelete={readOnly
                                  ? undefined
                                  : () => {
                                    setUnassignError(null);
                                    setUnassignTarget({ user, assignment: a });
                                  }}
                              />
                            ))}
                          </Box>
                        )}
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip
                          title={assignableEmployees.length === 0
                            ? t('customer.account.noActiveEmployees', 'Necesitas un empleado activo para asignar usuarios')
                            : ''}
                        >
                          <span>
                            <Button
                              size="small"
                              startIcon={<LinkIcon />}
                              onClick={() => openAssignDialog(user)}
                              disabled={readOnly || assignableEmployees.length === 0}
                              sx={{ textTransform: 'none' }}
                            >
                              {t('customer.account.assignToEmployee', 'Asignar a empleado')}
                            </Button>
                          </span>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Box>

        {/* Créditos */}
        <Box mb={4}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2} flexWrap="wrap" gap={1}>
            <Typography variant="h5" sx={{ fontWeight: 700 }}>
              {t('customer.account.credits', 'Créditos')}
            </Typography>
            {/* Selector de periodo: filtra movimientos y consumo. «Este
                ciclo» solo se ofrece con plan vigente (sin plan el backend
                degradaría a Todo). */}
            <ToggleButtonGroup
              size="small"
              exclusive
              value={creditsPeriod}
              onChange={handleCreditsPeriodChange}
            >
              {hasPlanCycle && (
                <ToggleButton value="cycle" sx={{ textTransform: 'none' }}>
                  {t('customer.account.period.cycle', 'Este ciclo')}
                </ToggleButton>
              )}
              <ToggleButton value="30d" sx={{ textTransform: 'none' }}>
                {t('customer.account.period.last30d', 'Últimos 30 días')}
              </ToggleButton>
              <ToggleButton value="all" sx={{ textTransform: 'none' }}>
                {t('customer.account.period.all', 'Todo')}
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>
          {/* Proyección: «a este ritmo tu saldo llega hasta el {fecha}». Va
              ANTES del resumen porque es la conclusión, no un detalle más — y
              solo se pinta con histórico suficiente (el componente y el backend
              comparten esa regla). En cuenta gestionada NO se ofrece comprar:
              lo accionable es escribir a su partner. */}
          <CreditProjectionCard
            projection={projection}
            managedBy={managedBy}
            onBuyCredits={managedBy ? undefined : () => navigate('/customer/plans')}
          />
          <Card elevation={0} sx={{ border: '1px solid #e0e0e0', borderRadius: 2, mb: 2 }}>
            <CardContent>
              {/* Desglose «dos bolsas» en TRES cifras claras (Plan que caduca /
                  Extra que no caduca / Total disponible): vive SOLO aquí — la
                  tarjeta de cabecera lleva únicamente la cifra disponible.
                  Sin campos nuevos, CreditBreakdown cae al saldo simple. */}
              <CreditBreakdown
                data={planData}
                balance={account?.balance ?? creditBalance}
              />
              {/* Consumido de la selección, etiquetado con el periodo EFECTIVO
                  que aplicó el backend; backend viejo (sin consumed_period) →
                  agregado histórico con etiqueta «Total». */}
              <Typography variant="body2" color="text.secondary" mt={1.5}>
                {creditsInfo?.consumed_period !== undefined && creditsInfo?.consumed_period !== null
                  ? periodConsumedLabel(creditsInfo.period ?? creditsPeriod)
                  : t('customer.account.consumedTotalLabel', 'Consumido (total)')}
                {': '}
                <Typography component="span" variant="body2" fontWeight={600} color="text.primary">
                  {formatCredits(creditsInfo?.consumed_period ?? account?.consumed_total)}
                </Typography>
              </Typography>
              {/* Frescura del consumo: el backend lo sincroniza de forma
                  perezosa (throttle de 5 min) y puede fallar sin que se note.
                  Se dice CUÁNDO se actualizó y se ofrece forzarlo. */}
              <Box display="flex" alignItems="center" gap={1} mt={1} flexWrap="wrap">
                <Typography
                  variant="caption"
                  color={consumptionSyncFailed ? 'warning.main' : 'text.secondary'}
                >
                  {consumptionSyncFailed
                    ? t('customer.account.consumptionSyncFailed', 'No se pudo actualizar el consumo; se muestra el último dato conocido')
                    : consumptionAgo
                      ? t('customer.account.consumptionUpdated', 'Consumo actualizado {{ago}}', { ago: consumptionAgo })
                      : t('customer.account.consumptionUpdatedUnknown', 'Consumo actualizado periódicamente')}
                </Typography>
                <Button
                  size="small"
                  startIcon={refreshingConsumption
                    ? <CircularProgress size={14} color="inherit" />
                    : <RefreshIcon fontSize="small" />}
                  onClick={handleRefreshConsumption}
                  disabled={readOnly || refreshingConsumption}
                  sx={{ textTransform: 'none' }}
                >
                  {t('customer.account.refreshConsumption', 'Actualizar')}
                </Button>
              </Box>
            </CardContent>
          </Card>
          {/* La tabla LARGA del ledger se ha mudado a su propia página
              (/customer/creditos), con paginación, filtros y orden — aquí puede
              haber muchísimos asientos. En «Mi cuenta» solo se muestran los
              últimos movimientos y un enlace a «Ver todo el historial». */}
          {creditEntries.length === 0 ? (
            <Card elevation={0} sx={{ border: '1px solid #e0e0e0', borderRadius: 2, textAlign: 'center', py: 4 }}>
              <Typography variant="body2" color="text.secondary">
                {t('customer.account.noCreditEntries', 'Sin movimientos todavía.')}
              </Typography>
            </Card>
          ) : (
            <>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                <Typography variant="subtitle2" color="text.secondary">
                  {t('customer.account.recentMovements', 'Últimos movimientos')}
                </Typography>
                <Button
                  size="small"
                  endIcon={<OpenInNewIcon fontSize="small" />}
                  onClick={() => navigate('/customer/creditos')}
                  sx={{ textTransform: 'none' }}
                >
                  {t('customer.account.viewAllHistory', 'Ver todo el historial')}
                </Button>
              </Box>
              <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid #e0e0e0', borderRadius: 2 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>{t('customer.account.fields.date', 'Fecha')}</TableCell>
                      <TableCell>{t('customer.account.fields.kind', 'Tipo')}</TableCell>
                      <TableCell>{t('customer.account.fields.concept', 'Concepto')}</TableCell>
                      <TableCell>{t('customer.account.fields.employee', 'Empleado')}</TableCell>
                      {/* CRÉDITOS: es la unidad del saldo. Poner «Importe» hacía leer
                          euros/dólares en una columna que nunca lleva dinero. */}
                      <TableCell align="right">{t('customer.account.fields.credits', 'Créditos')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {/* Solo los 5 más recientes (creditEntries ya viene ordenado
                        por id descendente del backend); el resto, en la página. */}
                    {creditEntries.slice(0, 5).map((entry) => {
                      const amount = Number(entry.amount);
                      return (
                        <TableRow key={entry.id} hover>
                          <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatCreditDateTime(entry.created_at)}</TableCell>
                          <TableCell>
                            <Chip label={creditKindLabel(entry.kind, t)} size="small" variant="outlined" />
                          </TableCell>
                          <TableCell>{entry.concept || '—'}</TableCell>
                          <TableCell>{employeeName(entry.account_employee_id)}</TableCell>
                          <TableCell align="right">
                            <Typography
                              variant="body2"
                              fontWeight={600}
                              color={amount < 0 ? 'error.main' : 'success.main'}
                            >
                              {amount > 0 ? '+' : ''}{formatCredits(entry.amount)}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
              {creditEntries.length > 5 && (
                <Box display="flex" justifyContent="center" mt={1.5}>
                  <Button
                    onClick={() => navigate('/customer/creditos')}
                    sx={{ textTransform: 'none' }}
                  >
                    {t('customer.account.viewAllHistory', 'Ver todo el historial')}
                  </Button>
                </Box>
              )}
            </>
          )}
        </Box>
      </Container>

      {/* Diálogo añadir usuario — comprueba el email al perder el foco y deja
          elegir CÓMO entrará el usuario (contraseña / contraseña y Google /
          solo Google). */}
      <Dialog open={addUserDialogOpen} onClose={() => setAddUserDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{t('customer.account.addUser', 'Añadir usuario')}</DialogTitle>
        <DialogContent>
          {addUserError && (
            <Alert severity="error" sx={{ mb: 2, mt: 1 }} onClose={() => setAddUserError(null)}>
              {addUserError}
            </Alert>
          )}
          <TextField
            autoFocus
            margin="dense"
            label={t('customer.account.fields.email', 'Email') + ' *'}
            type="email"
            fullWidth
            variant="outlined"
            value={newUserEmail}
            onChange={(e) => {
              setNewUserEmail(e.target.value);
              setEmailCheck(null); // el aviso anterior ya no aplica a este email
            }}
            onBlur={handleCheckEmail}
            error={newUserEmail !== '' && !isValidEmail(newUserEmail)}
            helperText={checkingEmail
              ? t('customer.account.checkingEmail', 'Comprobando el email…')
              : undefined}
            sx={{ mb: 2 }}
          />
          {/* Aviso: ese usuario ya existe en la plataforma. No se crea nadie
              nuevo ni se genera contraseña — solo se le añade a esta cuenta y
              a su proyecto. Solo se pinta si SE PUDO comprobar (checked). */}
          {emailCheck?.checked && emailCheck.exists && (
            <Alert severity="info" sx={{ mb: 2 }}>
              {t(
                'customer.account.userExistsWarning',
                'Este usuario ya existe en la plataforma: solo se añadirá a tu cuenta y a tu proyecto. No se creará una contraseña nueva ni se cambiará la suya.'
              )}
            </Alert>
          )}
          {emailCheck?.checked && emailCheck.in_account && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              {t('customer.account.userAlreadyInAccount', 'Ese email ya está en tu cuenta.')}
            </Alert>
          )}
          <TextField
            margin="dense"
            label={t('customer.account.fields.fullName', 'Nombre') + ' *'}
            fullWidth
            variant="outlined"
            value={newUserName}
            onChange={(e) => setNewUserName(e.target.value)}
            sx={{ mb: 2 }}
          />
          <FormControl fullWidth variant="outlined" margin="dense">
            <InputLabel>{t('customer.account.fields.authMethod', 'Cómo entrará')}</InputLabel>
            <Select
              value={newUserAuthMethod}
              onChange={(e) => setNewUserAuthMethod(e.target.value as AccountUserAuthMethod)}
              label={t('customer.account.fields.authMethod', 'Cómo entrará')}
              disabled={emailCheck?.checked && emailCheck.exists}
            >
              <MenuItem value="password">
                {t('customer.account.authMethod.password', 'Contraseña')}
              </MenuItem>
              <MenuItem value="password_google">
                {t('customer.account.authMethod.passwordGoogle', 'Contraseña y Google')}
              </MenuItem>
              <MenuItem value="google">
                {t('customer.account.authMethod.google', 'Solo Google')}
              </MenuItem>
            </Select>
          </FormControl>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
            {emailCheck?.checked && emailCheck.exists
              ? t('customer.account.authMethodExistingHelp', 'El usuario ya existe: conserva su forma de entrar actual.')
              : newUserAuthMethod === 'google'
                ? t('customer.account.authMethodGoogleHelp', 'Entrará con su cuenta de Google: no se genera contraseña y no habrá nada que compartir.')
                : newUserAuthMethod === 'password_google'
                  ? t('customer.account.authMethodBothHelp', 'Podrá entrar con la contraseña de un solo uso que te mostraremos o con su cuenta de Google.')
                  : t('customer.account.authMethodPasswordHelp', 'Se genera una contraseña de un solo uso que te mostraremos aquí y se le enviará por correo.')}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddUserDialogOpen(false)} sx={{ textTransform: 'none' }}>
            {t('common.cancel', 'Cancelar')}
          </Button>
          <Button
            onClick={handleAddUser}
            variant="contained"
            disabled={
              addUserLoading
              || !isValidEmail(newUserEmail)
              || newUserName.trim() === ''
              // Ya está en la cuenta: el backend respondería 409, mejor no dejarlo pulsar.
              || (emailCheck?.checked === true && emailCheck.in_account)
            }
            sx={{ textTransform: 'none', boxShadow: 'none' }}
          >
            {addUserLoading ? <CircularProgress size={20} color="inherit" /> : t('common.create', 'Crear')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Diálogo contratar empleado — picker por NOMBRE del catálogo público */}
      <Dialog open={hireDialogOpen} onClose={() => !hireLoading && setHireDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{t('customer.account.hireEmployee', 'Contratar empleado')}</DialogTitle>
        <DialogContent>
          {hireError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setHireError(null)}>
              {hireError}
            </Alert>
          )}
          {maxEmployees !== null && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              {t('customer.account.quota', 'Cupo: {{used}}/{{max}}', { used: usedSlots, max: maxEmployees })}
            </Typography>
          )}
          {/* ALMACENAMIENTO antes de contratar: primero de qué se parte (uso y
              límite de la cuenta) y justo debajo el TAMAÑO elegible, con lo que
              consume del incluido y lo que costará por ciclo. Enterarse de eso
              después, en la factura, es exactamente lo que hay que evitar. */}
          {storage && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1, mt: 1 }}>
              {t('customer.account.storageUsage', 'Almacenamiento: usas {{used}} de {{included}} GiB incluidos', {
                used: storage.used_gib,
                included: storage.included_gib
              })}
              {storage.max_gib > 0
                ? ' · ' + t('customer.account.storageMax', 'límite de tu cuenta: {{max}} GiB', { max: storage.max_gib })
                : ''}
            </Typography>
          )}
          {/* Sin usuarios NO se puede contratar: el empleado necesita un
              administrador, que es además la identidad con la que trabaja. El
              aviso lleva la acción (abre «Añadir usuario») para no dejar al
              cliente buscando dónde se hace. */}
          {adminCandidates.length === 0 && (
            <Alert
              severity="warning"
              sx={{ mb: 2, mt: 1 }}
              action={
                <Button
                  size="small"
                  color="inherit"
                  disabled={readOnly}
                  onClick={() => {
                    setHireDialogOpen(false);
                    openAddUserDialog();
                  }}
                  sx={{ textTransform: 'none', fontWeight: 600 }}
                >
                  {t('customer.account.addUser', 'Añadir usuario')}
                </Button>
              }
            >
              {t('customer.account.noAdminCandidates',
                'Antes de contratar, añade un usuario a tu cuenta: será el administrador del empleado y la identidad con la que trabaje. Puedes hacerlo en la sección «Usuarios» de esta página.')}
            </Alert>
          )}
          {/* El selector solo da nombres: el catálogo público es donde se ve
              qué hace cada empleado. Va JUNTO al picker, antes de elegir. */}
          <EmployeeCatalogLink sx={{ mt: 1, mb: 1 }} />
          <FormControl fullWidth variant="outlined" margin="dense" sx={{ mb: 2, mt: 1 }}>
            <InputLabel>{t('customer.account.fields.catalogEmployee', 'Empleado del catálogo')} *</InputLabel>
            <Select
              value={hireListingSlug}
              onChange={(e) => setHireListingSlug(e.target.value as string)}
              label={t('customer.account.fields.catalogEmployee', 'Empleado del catálogo') + ' *'}
              disabled={loadingListings}
            >
              <MenuItem value="" disabled>
                {loadingListings
                  ? t('common.loading', 'Cargando...')
                  : t('customer.account.selectEmployee', 'Selecciona un empleado')}
              </MenuItem>
              {listingOptions.map((option) => (
                <MenuItem key={option.slug} value={option.slug}>
                  {option.display_name || option.slug}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {/* Administrador del empleado — selector por NOMBRE (rellena el id) */}
          <FormControl fullWidth variant="outlined" margin="dense" sx={{ mb: 2 }}>
            <InputLabel>{t('customer.account.fields.employeeAdmin', 'Administrador del empleado')} *</InputLabel>
            <Select
              value={hireAdminUserId === '' ? '' : String(hireAdminUserId)}
              onChange={(e) => setHireAdminUserId(Number(e.target.value))}
              label={t('customer.account.fields.employeeAdmin', 'Administrador del empleado') + ' *'}
              disabled={adminCandidates.length === 0}
            >
              <MenuItem value="" disabled>
                {t('customer.account.selectAdmin', 'Selecciona un usuario de tu cuenta')}
              </MenuItem>
              {adminCandidates.map((u) => (
                <MenuItem key={u.id} value={String(u.id)}>
                  {u.full_name} · {u.email}
                </MenuItem>
              ))}
            </Select>
            <FormHelperText>
              {t('customer.account.employeeAdminHelp',
                'Administrará el empleado y será la identidad con la que este trabaje en la plataforma.')}
            </FormHelperText>
          </FormControl>
          <TextField
            margin="dense"
            label={t('customer.account.fields.displayName', 'Nombre para mostrar') + ' (' + t('common.optional', 'Opcional') + ')'}
            fullWidth
            variant="outlined"
            value={hireDisplayName}
            onChange={(e) => setHireDisplayName(e.target.value)}
            helperText={t('customer.account.displayNameHelp', 'Si lo dejas vacío se usará el nombre del catálogo')}
            sx={{ mb: 2 }}
          />
          {/* TAMAÑO de los dos discos del empleado, con su impacto en vivo. Va
              junto al resto de decisiones del alta y no en un paso posterior:
              ampliar después es permanente y puede tener cargo, nacer con el
              tamaño bueno no. */}
          <EmployeeStoragePicker
            storage={storage}
            value={hireVolumes}
            onChange={setHireVolumes}
            disabled={hireLoading}
          />
          {/* CAPACIDAD del empleado: cómo va a trabajar. Separada del disco
              porque no tiene ni techo ni cargo — es otra decisión. */}
          <EmployeeSizePicker
            value={hireSize}
            onChange={setHireSize}
            disabled={hireLoading}
          />
          <TextField
            margin="dense"
            label={t('customer.account.fields.creditsToAllocate', 'Límite mensual (créditos)') + ' (' + t('common.optional', 'Opcional') + ')'}
            type="number"
            fullWidth
            variant="outlined"
            value={hireCredits}
            onChange={(e) => setHireCredits(e.target.value)}
            inputProps={{ min: 0, step: '0.01' }}
            error={!hireCreditsValid}
            helperText={!hireCreditsValid
              ? t('customer.account.creditsInvalid', 'Introduce un número de créditos válido (0 o más)')
              : t('customer.account.limitHelp', 'Tope de gasto del empleado en una ventana de 30 días; vacío o 0 = sin límite propio.')}
            sx={{ mb: 2 }}
          />
          <Alert severity="info">
            {t('customer.account.allocateNote', 'Es un límite de gasto en ventana de 30 días, no una transferencia: tu saldo solo baja con el consumo real. Se aplica al (re)desplegar el empleado.')}
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setHireDialogOpen(false)} disabled={hireLoading} sx={{ textTransform: 'none' }}>
            {t('common.cancel', 'Cancelar')}
          </Button>
          <Button
            onClick={handleHireEmployee}
            variant="contained"
            disabled={hireLoading || !hireListingSlug || !hireCreditsValid || hireAdminUserId === ''
              // Techo de almacenamiento de la cuenta: el backend lo rechazaría
              // con 409, así que se frena aquí con el motivo ya visible arriba.
              || hireImpact.overCap}
            sx={{ textTransform: 'none', boxShadow: 'none' }}
          >
            {hireLoading ? <CircularProgress size={20} color="inherit" /> : t('customer.account.hire', 'Contratar')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Diálogo AMPLIAR ALMACENAMIENTO — permanente y con cargo por ciclo.
          Las dos advertencias van en el cuerpo, no en un tooltip: son la razón
          por la que esto no es un botón de un clic. */}
      <Dialog
        open={storageTarget !== null}
        onClose={() => !storageLoading && setStorageTarget(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {t('customer.account.expandStorage', 'Ampliar almacenamiento')}
        </DialogTitle>
        <DialogContent>
          {storageError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setStorageError(null)}>
              {storageError}
            </Alert>
          )}
          {storageTarget && (
            <>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                {t('customer.account.fields.employee', 'Empleado')}:{' '}
                <strong>{storageTarget.display_name || storageTarget.listing_slug}</strong>
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {t('customer.account.storageFromTo', 'Pasará de {{from}} a {{to}} GiB', {
                  from: employeeVolumes(storageTarget).ws + employeeVolumes(storageTarget).data,
                  to:
                    nextVolumes(storageTarget, storageSplit, storageStep).workspace_gib +
                    nextVolumes(storageTarget, storageSplit, storageStep).data_gib
                })}
                {': '}
                {t('customer.account.storageBreakdown', '({{ws}} espacio de trabajo + {{data}} datos)', {
                  ws: nextVolumes(storageTarget, storageSplit, storageStep).workspace_gib,
                  data: nextVolumes(storageTarget, storageSplit, storageStep).data_gib
                })}
              </Typography>
              {/* Reparto del tramo: las tres opciones suman exactamente un
                  tramo, así que ninguna puede dejar un tamaño inválido. Se
                  deshabilita la que rebasaría el tope por volumen. */}
              <FormControl fullWidth variant="outlined" margin="dense" sx={{ mb: 2 }}>
                <InputLabel>{t('customer.account.storageSplit', 'Dónde añadir el espacio')}</InputLabel>
                <Select
                  value={storageSplit}
                  onChange={(e) => setStorageSplit(e.target.value as StorageSplit)}
                  label={t('customer.account.storageSplit', 'Dónde añadir el espacio')}
                >
                  <MenuItem
                    value="both"
                    disabled={
                      nextVolumes(storageTarget, 'both', storageStep).workspace_gib > storageMaxVolume ||
                      nextVolumes(storageTarget, 'both', storageStep).data_gib > storageMaxVolume
                    }
                  >
                    {t('customer.account.storageSplitBoth', 'A partes iguales en los dos volúmenes')}
                  </MenuItem>
                  <MenuItem
                    value="workspace"
                    disabled={
                      nextVolumes(storageTarget, 'workspace', storageStep).workspace_gib > storageMaxVolume
                    }
                  >
                    {t('customer.account.storageSplitWorkspace', 'Todo al espacio de trabajo del agente')}
                  </MenuItem>
                  <MenuItem
                    value="data"
                    disabled={nextVolumes(storageTarget, 'data', storageStep).data_gib > storageMaxVolume}
                  >
                    {t('customer.account.storageSplitData', 'Todo a los datos de la aplicación')}
                  </MenuItem>
                </Select>
                <FormHelperText>
                  {t(
                    'customer.account.storageSplitHelp',
                    'El espacio de trabajo guarda lo que el agente produce; los datos, lo de su aplicación. Máximo {{max}} GiB por volumen.',
                    { max: storageMaxVolume }
                  )}
                </FormHelperText>
              </FormControl>
              {/* AVISO: permanente + cargo por ciclo. Severidad warning aunque
                  el tramo caiga dentro de lo incluido, porque lo irreversible
                  no depende del precio. */}
              <Alert severity="warning">
                <AlertTitle>
                  {t('customer.account.storageWarningTitle', 'Es permanente')}
                </AlertTitle>
                <Typography variant="body2">
                  {t(
                    'customer.account.storageWarningPermanent',
                    'El disco de un empleado no se puede reducir después: encogerlo obligaría a recrearlo perdiendo sus datos. Solo retirar el empleado libera el espacio.'
                  )}
                </Typography>
                <Typography variant="body2" sx={{ mt: 1 }}>
                  {/* En una cuenta GESTIONADA no se menciona coste, ni siquiera
                      el futuro: su almacenamiento lo paga el partner. Lo único
                      cierto —y lo único accionable— es que el espacio sale de
                      lo que ese partner tiene contratado. */}
                  {storage?.billed_to_partner
                    ? (managedBy
                        ? t(
                            'customer.account.storageWarningManaged',
                            'El espacio de tu cuenta lo gestiona {{name}}: esta ampliación sale de lo que tienen contratado. Si necesitas más, háblalo con ellos.',
                            { name: managedBy.name }
                          )
                        : t(
                            'customer.account.storageWarningManagedNoName',
                            'El espacio de tu cuenta lo gestiona quien te la administra: esta ampliación sale de lo que tienen contratado.'
                          ))
                    : storageExtraCost(storageStep) > 0
                      ? t(
                          'customer.account.storageWarningCost',
                          'Sumará {{credits}} créditos por ciclo a tu cuenta mientras el empleado exista.',
                          { credits: formatCredits(storageExtraCost(storageStep)) }
                        )
                      : t(
                          'customer.account.storageWarningFree',
                          'Cabe en el almacenamiento incluido de tu cuenta: hoy no genera cargo. Si más adelante te pasas de lo incluido, el exceso sí se cobrará por ciclo.'
                        )}
                </Typography>
                <Typography variant="body2" sx={{ mt: 1 }}>
                  {t(
                    'customer.account.storageWarningApply',
                    'Para que el disco crezca de verdad, el empleado se vuelve a desplegar al confirmar: puede quedarse unos segundos sin responder. Si ahora está parado, estrenará el tamaño nuevo en su próximo arranque.'
                  )}
                </Typography>
              </Alert>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setStorageTarget(null)}
            disabled={storageLoading}
            sx={{ textTransform: 'none' }}
          >
            {t('common.cancel', 'Cancelar')}
          </Button>
          <Button
            onClick={handleExpandStorage}
            variant="contained"
            color="warning"
            disabled={storageLoading || readOnly}
            sx={{ textTransform: 'none', boxShadow: 'none' }}
          >
            {storageLoading ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              t('customer.account.expandStorageConfirm', 'Ampliar +{{step}} GiB', { step: storageStep })
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Diálogo fijar límite mensual de un empleado — SIN tope por saldo:
          el límite no es una transferencia y puede superar el saldo. */}
      <Dialog open={allocateDialogOpen} onClose={() => !allocateLoading && setAllocateDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{t('customer.account.allocateCredits', 'Fijar límite mensual')}</DialogTitle>
        <DialogContent>
          {allocateError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setAllocateError(null)}>
              {allocateError}
            </Alert>
          )}
          {employeeToAllocate && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              {t('customer.account.fields.employee', 'Empleado')}:{' '}
              <strong>{employeeToAllocate.display_name || employeeToAllocate.listing_slug}</strong>
            </Typography>
          )}
          <TextField
            autoFocus
            margin="dense"
            label={t('customer.account.fields.credits', 'Límite mensual (créditos)') + ' *'}
            type="number"
            fullWidth
            variant="outlined"
            value={allocateValue}
            onChange={(e) => setAllocateValue(e.target.value)}
            inputProps={{ min: 0, step: '0.01' }}
            error={allocateValue !== '' && !allocateValid}
            helperText={t('customer.account.limitHelp', 'Tope de gasto del empleado en una ventana de 30 días; vacío o 0 = sin límite propio.')}
            sx={{ mb: 2 }}
          />
          <Alert severity="info">
            {t('customer.account.allocateNote', 'Es un límite de gasto en ventana de 30 días, no una transferencia: tu saldo solo baja con el consumo real. Se aplica al (re)desplegar el empleado.')}
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAllocateDialogOpen(false)} disabled={allocateLoading} sx={{ textTransform: 'none' }}>
            {t('common.cancel', 'Cancelar')}
          </Button>
          <Button
            onClick={handleAllocateCredits}
            variant="contained"
            disabled={allocateLoading || !allocateValid}
            sx={{ textTransform: 'none', boxShadow: 'none' }}
          >
            {allocateLoading ? <CircularProgress size={20} color="inherit" /> : t('customer.account.assign', 'Asignar')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Diálogo asignar usuario a empleado — selector por NOMBRE */}
      <Dialog open={assignDialogOpen} onClose={() => setAssignDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{t('customer.account.assignToEmployee', 'Asignar a empleado')}</DialogTitle>
        <DialogContent>
          {assignError && (
            <Alert severity="error" sx={{ mb: 2, mt: 1 }} onClose={() => setAssignError(null)}>
              {assignError}
            </Alert>
          )}
          {userToAssign && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {t('customer.account.assigningUser', 'Usuario')}: <strong>{userToAssign.email}</strong>
            </Typography>
          )}
          <FormControl fullWidth variant="outlined" margin="dense" sx={{ mb: 2 }}>
            <InputLabel>{t('customer.account.fields.employee', 'Empleado')} *</InputLabel>
            <Select
              value={assignEmployeeId}
              onChange={(e) => setAssignEmployeeId(e.target.value as number)}
              label={t('customer.account.fields.employee', 'Empleado') + ' *'}
            >
              {assignableEmployees.map((emp) => (
                <MenuItem key={emp.id} value={emp.id}>
                  {emp.display_name || emp.listing_slug}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth variant="outlined" margin="dense">
            <InputLabel>{t('customer.account.fields.role', 'Rol')}</InputLabel>
            <Select
              value={assignRole}
              onChange={(e) => setAssignRole(e.target.value as 'member' | 'admin')}
              label={t('customer.account.fields.role', 'Rol')}
            >
              <MenuItem value="member">{t('customer.account.role.member', 'Miembro')}</MenuItem>
              <MenuItem value="admin">{t('customer.account.role.admin', 'Administrador')}</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAssignDialogOpen(false)} sx={{ textTransform: 'none' }}>
            {t('common.cancel', 'Cancelar')}
          </Button>
          <Button
            onClick={handleAssignUser}
            variant="contained"
            disabled={assignLoading || assignEmployeeId === ''}
            sx={{ textTransform: 'none', boxShadow: 'none' }}
          >
            {assignLoading ? <CircularProgress size={20} color="inherit" /> : t('customer.account.assign', 'Asignar')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirmación ligera de desasignación (la X del chip) — DELETE
          /account/users/{userId}/assign/{accountEmployeeId} */}
      <Dialog
        open={unassignTarget !== null}
        onClose={() => !unassignLoading && setUnassignTarget(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>{t('customer.account.unassignTitle', 'Quitar asignación')}</DialogTitle>
        <DialogContent>
          {unassignError && (
            <Alert severity="error" sx={{ mb: 2, mt: 1 }} onClose={() => setUnassignError(null)}>
              {unassignError}
            </Alert>
          )}
          <Typography variant="body2">
            {t(
              'customer.account.unassignConfirm',
              '¿Quitar a {{user}} del empleado «{{employee}}»? Dejará de poder usarlo.',
              {
                user: unassignTarget?.user.full_name || unassignTarget?.user.email || '',
                employee: unassignTarget?.assignment.employee_name || ''
              }
            )}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setUnassignTarget(null)}
            disabled={unassignLoading}
            sx={{ textTransform: 'none' }}
          >
            {t('common.cancel', 'Cancelar')}
          </Button>
          <Button
            onClick={handleUnassignUser}
            variant="contained"
            color="error"
            disabled={unassignLoading}
            sx={{ textTransform: 'none', boxShadow: 'none' }}
          >
            {unassignLoading
              ? <CircularProgress size={20} color="inherit" />
              : t('customer.account.unassign', 'Quitar')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirmación de baja de empleado — DELETE /account/employees/{id}.
          Se explica lo que de verdad pasa: se retira la aplicación (con sus
          datos) y se LIBERA el cupo para contratar otro. */}
      <Dialog
        open={removeTarget !== null}
        onClose={() => !removeLoading && setRemoveTarget(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>{t('customer.account.removeEmployeeTitle', 'Eliminar empleado')}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" gutterBottom>
            {t(
              'customer.account.removeEmployeeConfirm',
              '¿Seguro que quieres eliminar a «{{employee}}»? Se retirará su aplicación y sus datos, y dejará de estar disponible para los usuarios asignados.',
              { employee: removeTarget?.display_name || removeTarget?.listing_slug || '' }
            )}
          </Typography>
          <Alert severity="warning" sx={{ mt: 1 }}>
            {t(
              'customer.account.removeEmployeeQuotaNote',
              'Se liberará una plaza de tu cupo, así que podrás contratar otro empleado. La acción no se puede deshacer.'
            )}
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setRemoveTarget(null)}
            disabled={removeLoading}
            sx={{ textTransform: 'none' }}
          >
            {t('common.cancel', 'Cancelar')}
          </Button>
          <Button
            onClick={handleRemoveEmployee}
            variant="contained"
            color="error"
            disabled={removeLoading}
            sx={{ textTransform: 'none', boxShadow: 'none' }}
          >
            {removeLoading
              ? <CircularProgress size={20} color="inherit" />
              : t('customer.account.removeEmployee', 'Eliminar')}
          </Button>
        </DialogActions>
      </Dialog>
    </CustomerLayout>
  );
};

export default CustomerAccountPage;
